// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

/**
 * M3 — Node inspector (shallow, stateless display) — Lagon reskin.
 *
 * The right-hand inspector panel of the agent detail page (design mockup
 * `Détail Agent.dc.html` §Node Inspector). It reflects the page-level
 * selection held in `<GraphCard>` and never fetches.
 *
 * Three selection shapes, mirroring the mockup:
 *   - the **input** endpoint → the agent's `input_schema` + the
 *     `state_vars` it initialises;
 *   - the **output** endpoint → the agent's `output_schema`;
 *   - a concrete **node** (LLM / gate / …) → its identity, description,
 *     reads/writes and its configured prompt pair (system + user).
 *
 * The input/output schemas live at the agent-model level (not on a
 * per-node wire shape) — the synthetic `input` / `output` endpoint nodes
 * stand in for the graph's `__start__` / `__end__` sentinels, so
 * selecting one surfaces the agent's contract here. Both prompts are read
 * from the node's opaque `configuration` sub-tree (`system_prompt` /
 * `user_prompt`) — the author-written pair, never the runtime-assembled
 * prompt — present for LLM nodes only; `—` otherwise.
 *
 * Visual values come from the Lagon design system (global.css tokens) —
 * no invented values.
 */
import { CheckSquare } from "lucide-react"
import { INPUT_ID, OUTPUT_ID } from "@/lib/mermaid-generator"

type EndpointId = typeof INPUT_ID | typeof OUTPUT_ID

/**
 * Safely read the `system_prompt` string out of a node's opaque
 * `configuration` sub-tree. Returns `null` when absent or not a string
 * (non-LLM nodes have no `system_prompt`), which the panel renders as
 * `—`.
 */
function readSystemPrompt(node: NodeModel): string | null {
  const value = node.configuration?.system_prompt
  return typeof value === "string" ? value : null
}

/**
 * Safely read the `user_prompt` string out of a node's opaque
 * `configuration` sub-tree. Returns `null` when absent or not a string,
 * which the panel renders as `—`. Note `""` is a first-class value (the
 * normal case for architect / meta-agent nodes fed via `reads`) and is
 * returned as-is, so the box shows `—` rather than a blank box.
 */
function readUserPrompt(node: NodeModel): string | null {
  const value = node.configuration?.user_prompt
  return typeof value === "string" ? value : null
}

/** A mono uppercase section eyebrow (mockup inspector labels). */
function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <span className="font-mono text-[10.5px] uppercase tracking-[0.06em] text-text-tertiary">
      {children}
    </span>
  )
}

/** Shared labelled prompt box (mockup prompt-box style). */
function PromptBox({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Eyebrow>{label}</Eyebrow>
      <div className="max-h-[12em] overflow-auto whitespace-pre-wrap rounded-md border border-border bg-[#02100F] px-3.5 py-3 font-mono text-xs leading-[1.65] text-text-secondary">
        {children}
      </div>
    </div>
  )
}

/** Shared code-box for JSON schemas (mockup schema-box style). */
function SchemaBox({ value }: { value: unknown }) {
  return (
    <div className="max-h-[210px] overflow-auto whitespace-pre rounded-md border border-border bg-[#02100F] px-3.5 py-3 font-mono text-xs leading-[1.6] text-text-secondary">
      {JSON.stringify(value, null, 2)}
    </div>
  )
}

/**
 * Render one state-var entry. The wire shape is a structured record
 * (snake_case mirror of ebiose-core `StateVar`: `name`, `type`, `doc`,
 * …) — show `name: type`; a bare string passes through; anything else
 * falls back to JSON so nothing ever renders as `[object Object]`.
 */
function formatStateVar(value: unknown): string {
  if (typeof value === "string") return value
  if (typeof value === "object" && value !== null && "name" in value) {
    const record = value as { name: unknown; type?: unknown }
    const name = String(record.name)
    return typeof record.type === "string" && record.type !== ""
      ? `${name}: ${record.type}`
      : name
  }
  return JSON.stringify(value)
}

/** The selected element's name + a muted synthetic/real type label. */
function SelectionHeading({ name, type }: { name: string; type: string }) {
  return (
    <div className="flex flex-row flex-wrap items-baseline gap-2">
      <span className="font-mono text-sm text-primary [text-shadow:0_0_14px_rgba(69,240,200,0.4)]">
        {name}
      </span>
      <span className="text-[11px] text-text-tertiary">{type}</span>
    </div>
  )
}

/** Shell — region wrapper + the panel title (the inspector's a11y name). */
function Shell({ children }: { children: React.ReactNode }) {
  return (
    <section
      aria-label="Node inspector"
      className="box flex min-w-0 flex-col gap-3 p-[18px]"
    >
      <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-text-tertiary">
        Node Inspector
      </span>
      {children}
    </section>
  )
}

/** The `input` endpoint → agent input_schema + initialised state vars. */
function InputView({ model }: { model: GraphAgentModel }) {
  // debt: still reads the retired state_vars shape, drop that arm once the DEV-470 migration has run everywhere
  const stateVars = model.state_vars ?? model.custom_state_vars ?? []
  return (
    <div className="flex flex-col gap-3">
      <SelectionHeading name="input" type="InputNode" />
      <p className="text-[12.5px] leading-[1.55] text-text-secondary">
        Entry point — the payload the agent receives and the state it
        initialises.
      </p>
      <div className="flex flex-col gap-1.5">
        <Eyebrow>input_schema</Eyebrow>
        <SchemaBox value={model.input_schema} />
      </div>
      <div className="flex flex-col gap-1.5">
        <Eyebrow>state_vars</Eyebrow>
        {stateVars.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {stateVars.map((v, i) => (
              <span
                key={i}
                className="inline-flex items-center rounded-full border border-primary/25 bg-primary/[0.08] px-2.5 py-1 font-mono text-[11.5px] text-primary"
              >
                {formatStateVar(v)}
              </span>
            ))}
          </div>
        ) : (
          <p className="font-mono text-sm text-text-tertiary">—</p>
        )}
      </div>
    </div>
  )
}

/** The `output` endpoint → agent output_schema. */
function OutputView({ model }: { model: GraphAgentModel }) {
  return (
    <div className="flex flex-col gap-3">
      <SelectionHeading name="output" type="OutputNode" />
      <p className="text-[12.5px] leading-[1.55] text-text-secondary">
        Exit point — the final response returned to the caller.
      </p>
      <div className="flex flex-col gap-1.5">
        <Eyebrow>output_schema</Eyebrow>
        <SchemaBox value={model.output_schema} />
      </div>
    </div>
  )
}

/** A concrete node → identity, description, reads/writes, system prompt. */
function NodeView({ node }: { node: NodeModel }) {
  const systemPrompt = readSystemPrompt(node)
  const userPrompt = readUserPrompt(node)
  return (
    <div className="flex flex-col gap-3">
      <SelectionHeading name={node.name} type={node.node_type} />

      {node.description ? (
        <p className="text-[12.5px] leading-[1.55] text-text-secondary">
          {node.description}
        </p>
      ) : null}

      <dl className="grid grid-cols-[max-content_1fr] items-baseline gap-x-3.5 gap-y-[7px] font-mono text-xs">
        <dt className="text-text-tertiary">reads</dt>
        <dd className="text-llm">
          {node.reads.length > 0 ? node.reads.join(", ") : "—"}
        </dd>
        <dt className="text-text-tertiary">writes</dt>
        <dd className="text-[#8FE07F]">
          {node.writes.length > 0 ? node.writes.join(", ") : "—"}
        </dd>
      </dl>

      <PromptBox label="system prompt">{systemPrompt ?? "—"}</PromptBox>
      <PromptBox label="user prompt">{userPrompt ? userPrompt : "—"}</PromptBox>
    </div>
  )
}

export function NodeInspector({
  node,
  endpoint,
  model,
}: {
  node: NodeModel | null
  endpoint: EndpointId | null
  model: GraphAgentModel
}) {
  if (endpoint === INPUT_ID) {
    return (
      <Shell>
        <InputView model={model} />
      </Shell>
    )
  }
  if (endpoint === OUTPUT_ID) {
    return (
      <Shell>
        <OutputView model={model} />
      </Shell>
    )
  }
  if (node) {
    return (
      <Shell>
        <NodeView node={node} />
      </Shell>
    )
  }
  return (
    <Shell>
      <div className="flex flex-1 flex-col items-center justify-center gap-3 px-2.5 py-[30px] text-center">
        <div className="flex h-[46px] w-[46px] items-center justify-center rounded-md border border-dashed border-border-active text-border-active">
          <CheckSquare className="h-[22px] w-[22px]" />
        </div>
        <span className="text-[13px] text-text-tertiary">
          click a node in the graph to inspect it
        </span>
      </div>
    </Shell>
  )
}
